import PropTypes from 'prop-types';
import React from 'react';
import Flexbox from 'flexbox-react';
import { css } from 'emotion';
import EntityHeader from 'components/core/EntityHeader';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import Toolbar from 'components/core/toolbar/Toolbar';
import AbstractForm from 'components/page/process/AbstractForm';
import ProcessUtils from 'utils/ProcessUtils';
import ComponentUtils from 'utils/ComponentUtils';
import ProcessValidator from 'utils/ProcessValidator';
import OutputDatastreamVariableMappings from 'components/page/process/variableRetrieval/outputs/OutputDatastreamVariableMappings';
import ModalsService from 'services/ModalsService';
import OutputDatastreamNameField from 'components/core/fields/OutputDatastreamNameField';
import OutputIntervalField from 'components/core/fields/OutputIntervalField';
import OutputIntervalTimezoneField from 'components/core/fields/OutputIntervalTimezoneField';
import { colorTextGrey } from 'styles/base';
import $ from 'jquery';
import _ from 'lodash';

const nameStyle = css`
    flex-grow: 1;
    margin-bottom: 10px;
    span.editable-component {
        font-weight:700;
    }
`;

const fieldRowStyle = css`
    margin-bottom: 10px;
`;

/**
 * The form that renders details for an output datastream
 */
export default class OutputDatastreamForm extends AbstractForm {

    static propTypes = {
        process: PropTypes.object,
        processErrors: PropTypes.object,
        revisionNum: PropTypes.string,
        dods: PropTypes.array
    };

    constructor(props) {
        super(props);
        this.validator = new ProcessValidator(props.process);
        this._initializePropDependentVariables(props);
    }

    componentWillUpdate(nextProps) {
        this._initializePropDependentVariables(nextProps);
    }

    // shouldComponentUpdate(nextProps, nextState) {
    //     // componentWillReceiveProps is called twice when renaming, first time its called props.routeParams.entity has old name but
    //     // props.process has new name and so we were getting 'undefined' JS error in console. thats why we're checking for 'undefined' here
    //     if (nextProps.process.variable_retrieval.output_datastream_variable_mappings[nextProps.routeParams.entity]) {
    //         return true;
    //     }
    //     return false;
    // }

    _initializePropDependentVariables(props) {
        this.outputDatastreamName = props.match.params.entity;
        this.processId = props.match.params.processId;
        this.outputDatastreamMappings = props.process.variable_retrieval.output_datastream_variable_mappings[this.outputDatastreamName];
        if (!this.outputDatastreamMappings) {
            this.outputDatastreamMappings = {};
        }

        // There may be no DOD defined if this is a "DOD on the Fly"
        this.outputstreamVariables = [];
        if (ProcessUtils.getDatastreamVariablesForDod(this.outputDatastreamName)) {
            this.outputstreamVariables = Object.keys(ProcessUtils.getDatastreamVariablesForDod(this.outputDatastreamName));
        }

        this.sortVariableMappings();
        this.setCoordinateSystemList();
        this.baseFieldPath = ['variable_retrieval', 'output_datastream_variable_mappings', this.outputDatastreamName];

        // Determine the used variables (i.e., any variable who has been mapped to this datastream)
        this.usedVars = [];
        Object.keys(this.outputDatastreamMappings).forEach(variableName => {
            this.usedVars.push(variableName);
        });

        this.validator = new ProcessValidator(props.process);
    }

    sortVariableMappings() {
        this.sortedIds = [];
        this.variableMappings = [];

        if (this.outputDatastreamMappings) {
            $.each(this.outputDatastreamMappings, (key, value) => {
                this.sortedIds.push(key);
                this.variableMappings.push({ retrievedVar: key, outputVar: value });
            });

            // Sort the list in alphabetical order
            this.sortedIds = this.sortedIds.sort((a, b) => {
                return a.toLowerCase().localeCompare(b.toLowerCase());
            });

            this.variableMappings = this.variableMappings.sort((a, b) => {
                return a.retrievedVar.toLowerCase().localeCompare(b.retrievedVar.toLowerCase());
            });
        }
    }

    getRetrievedVariableCoordinateSystem = (name) => {
        return _.get(this.props.process, `variable_retrieval.retrieved_variables.${name}.coordinate_system`);
    };

    setCoordinateSystemList = () => {
        const vars = Object.keys(this.outputDatastreamMappings);
        this.coordinateSystems = {};
        vars.forEach((v) => {
            const coordinateSystem = this.getRetrievedVariableCoordinateSystem(v);
            if (coordinateSystem) {
                if (!_.includes(Object.keys(this.coordinateSystems), coordinateSystem)) {
                    this.coordinateSystems[coordinateSystem] = this.props.process.variable_retrieval.coordinate_systems[coordinateSystem];
                }
            }
        });
    };

    onNewVariableMappingClicked = () => {
        const allVars = Object.keys(this.props.process.variable_retrieval.retrieved_variables);
        const options = _.difference(allVars, this.usedVars).map((v) => {
            return { value: v, label: v };
        });

        const isInputValid = (newNames) => {
            return this.validator.isEntityNull(newNames, 'Retrieved variable');
        };

        if (options.length === 0) {
            let message = 'All process variables have already been assigned to this output datastream.';
            if (allVars.length === 0) {
                message = 'No variables have been defined for this process yet.';
            }
            ModalsService.openModal({
                id: 'all_variables_assigned_message',
                modalType: ModalsService.TYPE_INFO,
                modalTitle: 'Assign Variables to Output Datastream',
                modalMessage: message
            });
        } else {
            ComponentUtils.openNewEntityModal(
                'Variable Mapping',
                (selectedVariableNames) => {
                    const editor = ProcessUtils.getEditor(this.props.process);
                    const datastreamName = this.props.match.params.entity;
                    selectedVariableNames.forEach(retrievedVarName => {
                        editor.addOutputDatastreamToVariable(datastreamName, retrievedVarName);
                    });
                    editor.commit();
                },
                isInputValid,
                {
                    options,
                    newSelectionPrefix: 'Retrieved variable:',
                    inputLabel: 'Retrieved variable',
                    creatable: false,
                    multi: true }
            );
        }
    };

    onRemoveVariableMappingClicked = (selectedItems) => {
        ComponentUtils.openDeleteEntitiesModal(this.props.process, this.baseFieldPath, selectedItems,
            this.validator.canOutputDatastreamMappingBeDeleted);
    };

    onDeleteDatastreamClicked = () => {
        ComponentUtils.openDeleteEntitiesModal(this.props.process, 'output_datastreams', this.outputDatastreamName,
            this.validator.canOutputDatastreamsBeDeleted, true);
    }

    render() {

        const subTextStyles = css`
            color: ${colorTextGrey};
            font-style: italic;
            padding: 3px 15px 9px 15px;
        `;

        return (
            <Flexbox className="output-datastream-form" flexGrow={1} flexDirection="row">
                <Flexbox flexDirection="column" flexGrow={2}>
                    <Flexbox flexDirection={'row'} alignItems="flex-start" flexGrow={1}>
                        <OutputDatastreamNameField
                            value={this.outputDatastreamName}
                            process={this.props.process}
                            dods={this.props.dods}
                            processErrors={this.props.processErrors}
                            hasWritePermissions={this.props.hasWritePermissions}
                            className={`noshrink ${nameStyle}`}
                        />
                        <Toolbar>
                            <ToolbarItem
                                title="Delete output datastream"
                                className="fa fa-times delete"
                                clickHandler={this.onDeleteDatastreamClicked}
                                enabled={this.props.hasWritePermissions}
                                showText
                            />
                        </Toolbar>
                    </Flexbox>
                    <OutputIntervalField
                        outputDatastreamName={this.outputDatastreamName}
                        process={this.props.process}
                        dods={this.props.dods}
                        processErrors={this.props.processErrors}
                        hasWritePermissions={this.props.hasWritePermissions}
                        className={`noshrink ${fieldRowStyle}`}
                    />
                    <OutputIntervalTimezoneField
                        outputDatastreamName={this.outputDatastreamName}
                        process={this.props.process}
                        dods={this.props.dods}
                        processErrors={this.props.processErrors}
                        hasWritePermissions={this.props.hasWritePermissions}
                        className={`noshrink ${fieldRowStyle}`}
                    />
                    <Flexbox flexDirection="row" height="100%">
                        <Flexbox flexDirection="column" marginTop="12px" className="entity-box variable-mappings">
                            <OutputDatastreamVariableMappings
                                id={`${this.output}-variable-mappings`}
                                process={this.props.process}
                                navId={this.processId}
                                revisionNum={this.props.revisionNum}
                                processErrors={this.props.processErrors}
                                datastream={this.outputDatastreamName}
                                sortedIds={this.sortedIds}
                                data={this.outputDatastreamMappings}
                                outputstreamVariables={this.outputstreamVariables}
                                getBackgroundColor={this.props.getBackgroundColor}
                                getCoordinateSystem={this.getRetrievedVariableCoordinateSystem}
                                baseFieldPath={this.baseFieldPath}
                                hasWritePermissions={this.props.hasWritePermissions}
                                filterable
                                subText={
                                    <Flexbox className={subTextStyles}>
                                        If not all output variables are paired with a retrieved variable, only the variables mapped will be in the output if run with DOD on the fly command line option. Otherwise all variables in DOD that are not mapped will be in output as missing values.
                                    </Flexbox>
                                }
                            >
                                <EntityHeader title="Output Variables" count={this.variableMappings.length}>
                                    <Toolbar>
                                        <ToolbarItem
                                            title="Remove retrieved variable from output datastream"
                                            className="fa fa-times delete"
                                            clickHandler={this.onRemoveVariableMappingClicked}
                                            selectionDependent
                                            enabled={this.props.hasWritePermissions}
                                        />
                                        <ToolbarItem
                                            title="Select Retrieved Variable"
                                            className="fa fa-plus new"
                                            clickHandler={this.onNewVariableMappingClicked}
                                            enabled={this.props.hasWritePermissions}
                                            showText
                                        />
                                    </Toolbar>
                                </EntityHeader>
                            </OutputDatastreamVariableMappings>
                        </Flexbox>
                    </Flexbox>
                </Flexbox>
            </Flexbox>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/page/process/variableRetrieval/outputs/OutputDatastreamForm.js